import { Button, Link, Modal } from "north.js";
import React, { useState } from "react";
import styled from "styled-components";

import { EVENT_ID } from "src/shared/constants/event";
import { useEventContext } from "src/shared/contexts";
import { useUserContext } from "src/shared/contexts/UserContext";
import { useGetMeLazyQuery } from "src/shared/contexts/UserContext/graphql/getMe.generated";
import { SlackIcon } from "src/static/icons";

import { useClearEventPlatformIdMutation } from "./graphql/clearEventPlatformId.generated";

interface SlackAccountLinkButtonProps {
  fullWidth?: boolean;
  size?: "md" | "lg";
}

const SlackAccountLinkButton: React.FC<SlackAccountLinkButtonProps> = ({
  fullWidth = false,
  size = "lg",
}) => {
  const { info } = useUserContext();
  const { hackerDirectory } = useEventContext();
  const slackConnectEnabled = hackerDirectory.slackLinkingEnabled;
  const [isSlackModalOpen, setIsSlackModalOpen] = useState(false);
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [hasCheckedSlack, setHasCheckedSlack] = useState(false);
  const [unlinkError, setUnlinkError] = useState(false);
  const [refreshSlack, { data, loading, error }] = useGetMeLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: ({ me }) => setHasCheckedSlack(!me.event_platform_user_id),
    onError: () => setHasCheckedSlack(true),
  });
  const [clearEventPlatformId, { loading: unlinking }] =
    useClearEventPlatformIdMutation();
  const isSlackConnected = Boolean(
    data ? data.me.event_platform_user_id : info?.event_platform_user_id
  );
  const tooltipId = `profile-slack-linking-tooltip-${
    fullWidth ? "full-width" : "inline"
  }`;

  return (
    <>
      {slackConnectEnabled ? (
        <SlackButton
          $connected={isSlackConnected}
          $fullWidth={fullWidth}
          type="button"
          color={isSlackConnected ? "success" : "secondary"}
          size={size}
          trailingIcon={<StyledSlackIcon aria-hidden />}
          onClick={() => {
            setHasCheckedSlack(false);
            setIsSlackModalOpen(true);
          }}
        >
          {isSlackConnected ? "Slack connected" : "Connect to Slack"}
        </SlackButton>
      ) : (
        <DisabledTooltipTarget
          $fullWidth={fullWidth}
          tabIndex={0}
          aria-describedby={tooltipId}
        >
          <SlackDisabled
            $fullWidth={fullWidth}
            type="button"
            disabled
            aria-disabled
            aria-label="Connect to Slack (disabled until the directory opens)"
          >
            <SlackDisabledText>Connect to Slack</SlackDisabledText>
            <StyledSlackIcon aria-hidden />
          </SlackDisabled>
          <SlackLinkingTooltip id={tooltipId} role="tooltip">
            slack linking coming soon!
          </SlackLinkingTooltip>
        </DisabledTooltipTarget>
      )}

      {slackConnectEnabled && (
        <>
          <Modal
            open={isSlackModalOpen}
            onClose={() => setIsSlackModalOpen(false)}
            style={{ width: "min(520px, 90vw)" }}
          >
            <Modal.Header>Connect to Slack</Modal.Header>
            <Modal.Body>
              <ModalText>
                Follow the Slack account-linking instructions, then return here
                to refresh your connection status.
              </ModalText>
              <Link
                color="secondary"
                href="https://docs.google.com/document/d/1oh2IM-mRHjKx17uLrImOEcXKbg6SQIHI6ITLfgx9qNE/edit?tab=t.0"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Slack linking instructions
              </Link>
              {error ? (
                <RefreshStatus $error>
                  We couldn&apos;t refresh your Slack status. Please try again.
                </RefreshStatus>
              ) : isSlackConnected ? (
                <RefreshStatus $connected>Slack connected.</RefreshStatus>
              ) : (
                hasCheckedSlack && (
                  <RefreshStatus>
                    Slack is not linked yet. Finish the instructions and try
                    again.
                  </RefreshStatus>
                )
              )}
            </Modal.Body>
            <Modal.Footer justify={isSlackConnected ? "space-between" : "end"}>
              {isSlackConnected && (
                <Button
                  size="md"
                  color="danger"
                  onClick={() => {
                    setIsSlackModalOpen(false);
                    setIsUnlinkModalOpen(true);
                  }}
                >
                  Unlink Slack
                </Button>
              )}
              <Button
                size="md"
                color="secondary"
                disabled={loading}
                onClick={() => {
                  setHasCheckedSlack(false);
                  refreshSlack({ variables: { eventId: EVENT_ID } });
                }}
              >
                {loading ? "Refreshing..." : "Refresh status"}
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal
            open={isUnlinkModalOpen}
            onClose={() => {
              setUnlinkError(false);
              setIsUnlinkModalOpen(false);
              setIsSlackModalOpen(true);
            }}
            style={{ width: "min(520px, 90vw)" }}
          >
            <Modal.Header>Unlink Slack</Modal.Header>
            <Modal.Body>
              <UnlinkModalText>
                Are you sure you want to unlink your Slack account? You can
                reconnect it later.
              </UnlinkModalText>
              {unlinkError && (
                <UnlinkErrorText>
                  We couldn&apos;t unlink your Slack account. Please try again.
                </UnlinkErrorText>
              )}
            </Modal.Body>
            <Modal.Footer justify="end">
              <Button
                size="md"
                color="secondary"
                disabled={unlinking}
                onClick={() => {
                  setUnlinkError(false);
                  setIsUnlinkModalOpen(false);
                  setIsSlackModalOpen(true);
                }}
              >
                Cancel
              </Button>
              <Button
                size="md"
                color="danger"
                disabled={unlinking}
                onClick={async () => {
                  setUnlinkError(false);
                  try {
                    await clearEventPlatformId();
                    setIsUnlinkModalOpen(false);
                  } catch {
                    setUnlinkError(true);
                  }
                }}
              >
                {unlinking ? "Unlinking..." : "Unlink Slack"}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
};

export default SlackAccountLinkButton;

const SlackButton = styled(Button)<{
  $connected: boolean;
  $fullWidth: boolean;
}>`
  && {
    width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "fit-content")};
    min-height: 48px;
    align-self: flex-start;

    ${({ theme, $connected }) =>
      $connected &&
      `
        color: ${theme.globalConstants.color.black};
        background-color: ${theme.globalConstants.color.backgroundSuccessLight};
        border-color: ${theme.globalConstants.color.borderSuccess};

        &:hover {
          background-color: ${theme.globalConstants.color.backgroundSuccessLight};
        }
      `}
  }
`;

const SlackDisabled = styled.button<{ $fullWidth: boolean }>`
  box-sizing: border-box;
  display: flex;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "fit-content")};
  min-height: 48px;
  padding: 12px 16px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  cursor: not-allowed;
  opacity: 1;
`;

const DisabledTooltipTarget = styled.span<{ $fullWidth: boolean }>`
  position: relative;
  display: ${({ $fullWidth }) => ($fullWidth ? "flex" : "inline-flex")};
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "fit-content")};
  align-self: flex-start;

  &:hover [role="tooltip"],
  &:focus [role="tooltip"] {
    visibility: visible;
    opacity: 1;
  }
`;

const SlackLinkingTooltip = styled.span`
  position: absolute;
  z-index: 2;
  top: calc(100% + 8px);
  left: 50%;
  width: max-content;
  max-width: 200px;
  padding: 8px 12px;
  transform: translateX(-50%);
  visibility: hidden;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 2px rgb(0 0 0 / 6%), 0 1px 3px rgb(0 0 0 / 10%);
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease, visibility 120ms ease;
`;

const SlackDisabledText = styled.span`
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
`;

const StyledSlackIcon = styled(SlackIcon)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const ModalText = styled.p`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
`;

const UnlinkModalText = styled(ModalText)`
  margin: 0;
`;

const UnlinkErrorText = styled(UnlinkModalText)`
  margin-top: 12px;
  color: ${({ theme }) => theme.globalConstants.color.textDanger};
`;

const RefreshStatus = styled.p<{
  $connected?: boolean;
  $error?: boolean;
}>`
  margin: 12px 0 0;
  color: ${({ theme, $connected, $error }) =>
    $error
      ? theme.globalConstants.color.textDanger
      : $connected
      ? theme.globalConstants.color.textSuccess
      : theme.globalConstants.color.textWarning};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
`;
